import classNames from "classnames";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  aggregateReactions,
  AggregatedReaction,
  ReactionArg,
  ReactionLabels,
} from "./reactions";

// Show at most this many distinct emoji badges inline; the rest collapse into a
// single "+N" overflow badge.
const MAX_VISIBLE = 5;

type Props = {
  reactions: ReactionArg[];
  myId: string;
  onToggle: (emoji: string) => void;
  labels: ReactionLabels;
};

// Aggregated reaction badges. Each badge shows emoji + count and, when tapped,
// reveals who reacted (and a remove action for your own reaction). Excess
// emojis collapse into a "+N" badge.
export default function ReactionBadges({
  reactions,
  myId,
  onToggle,
  labels,
}: Props) {
  const aggregated = aggregateReactions(reactions, myId);
  if (aggregated.length === 0) return null;

  const overflowing = aggregated.length > MAX_VISIBLE;
  const visible = overflowing ? aggregated.slice(0, MAX_VISIBLE) : aggregated;
  const overflow = overflowing ? aggregated.slice(MAX_VISIBLE) : [];

  return (
    <div className="reactions__badges flex flex-wrap items-center gap-1">
      {visible.map((reaction) => (
        <ReactionBadge
          key={reaction.emoji}
          reaction={reaction}
          onToggle={onToggle}
          labels={labels}
        />
      ))}
      {overflow.length > 0 && <OverflowBadge reactions={overflow} />}
    </div>
  );
}

function ReactionBadge({
  reaction: { emoji, count, members, mine },
  onToggle,
  labels,
}: {
  reaction: AggregatedReaction;
  onToggle: (emoji: string) => void;
  labels: ReactionLabels;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-pressed={mine}
          className={classNames(
            "inline-flex cursor-pointer items-center gap-1 rounded-full px-2.5 py-1 text-sm leading-none ring-1 ring-inset transition-colors",
            mine
              ? "bg-primary/10 text-primary ring-primary/20 hover:bg-primary/15"
              : "bg-gray-100 text-gray-600 ring-black/5 hover:bg-gray-200",
          )}
        >
          <span className="text-base leading-none">{emoji}</span>
          <span className="font-medium tabular-nums leading-none">{count}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-64 w-auto max-w-xs overflow-y-auto rounded border bg-white p-2 shadow-lg"
      >
        <div className="mb-1 flex items-center gap-1 text-xs font-semibold">
          <span className="text-sm">{emoji}</span>
          <span>{labels.reactedBy}</span>
        </div>
        <ul className="text-sm">
          {members.map((member, index) => (
            <li key={`${member.id}-${index}`} className="py-0.5">
              {member.name}
            </li>
          ))}
        </ul>
        {mine && (
          <button
            type="button"
            className="mt-2 cursor-pointer text-xs text-primary hover:underline"
            onClick={() => onToggle(emoji)}
          >
            {labels.remove}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Collapsed "+N" badge for the remaining emojis; its popover lists the full
// per-emoji breakdown with the reactors' names.
function OverflowBadge({ reactions }: { reactions: AggregatedReaction[] }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex cursor-pointer items-center rounded-full bg-gray-100 px-2.5 py-1 text-sm font-medium leading-none text-gray-600 ring-1 ring-inset ring-black/5 transition-colors hover:bg-gray-200"
        >
          +{reactions.length}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-64 w-auto max-w-xs overflow-y-auto rounded border bg-white p-2 shadow-lg"
      >
        <ul className="text-sm">
          {reactions.map(({ emoji, count, members }) => (
            <li key={emoji} className="flex items-start gap-2 py-0.5">
              <span className="text-sm leading-5">{emoji}</span>
              <span className="font-medium tabular-nums leading-5">
                {count}
              </span>
              <span className="text-muted">
                {members.map((member) => member.name).join(", ")}
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
